import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'antd';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Field, Indicator, Input } from 'components/common';

import { useAdvertisersQuery } from 'modules/apiData/dataApiSlice';
import { useAuth } from 'modules/auth/useAuth';
import { useOrganization } from 'modules/organization/useOrganization';
import { UserRoles } from 'modules/user/userApiTypes';

import { DSP_ADFORM, DSP_LABELS, DSP_XANDR } from '../../utils/constants';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY } from '../../utils/currencyConstants';
import {
  AdvertiserFormInputs,
  EditAdvertisersModalProps,
  MAX_LENGTH,
  editSchema,
} from './advertiserSettingsTypes';

export const EditAdvertisersModal = ({
  onClose,
  selectedAdvertiser,
}: EditAdvertisersModalProps) => {
  const {
    getCurrentAgency,
    currentAgency,
    currentAgencyId,
    agencies,
    updateAdvertiser,
    errorText,
    dsp,
  } = useOrganization();
  const { role } = useAuth();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<AdvertiserFormInputs>({
    resolver: yupResolver(editSchema),
    values: {
      name: selectedAdvertiser?.name || '',
      currency: selectedAdvertiser?.currency || DEFAULT_CURRENCY,
      linkedAdvertiserId: selectedAdvertiser?.linkedAdvertiserId || '',
      agencyId: currentAgencyId || '',
    },
  });

  // Moving an advertiser created under the wrong agency (CAA-461). Same roles as
  // PUT /dsp-advertiser/{id}/agency; the backend also requires an ADMIN to be in both agencies,
  // which for an ADMIN is exactly the agency list loaded here.
  const canMove =
    (role === UserRoles.SUPER_ADMIN.value ||
      role === UserRoles.INTERNAL_ADMIN.value ||
      role === UserRoles.ADMIN.value) &&
    agencies.length > 1;
  const targetAgencyId = watch('agencyId');
  const isMoving = canMove && !!targetAgencyId && targetAgencyId !== currentAgencyId;
  const agencyName = (id?: string | null) =>
    agencies.find((a) => a.id === id)?.name || 'another agency';

  const { data: xandrAdvertisers, isLoading: isLoadingXandr } = useAdvertisersQuery(DSP_XANDR, {
    skip: dsp !== DSP_ADFORM,
  });

  useEffect(() => {
    !currentAgency && getCurrentAgency();
  }, [getCurrentAgency, currentAgency]);

  const handleFormSubmit = handleSubmit(({ name, currency, linkedAdvertiserId, agencyId }) => {
    updateAdvertiser({
      name,
      advertiserId: selectedAdvertiser?.id || '',
      currency: currency || DEFAULT_CURRENCY,
      linkedAdvertiserId: linkedAdvertiserId || undefined,
      dsp: dsp || '',
      agencyId: isMoving ? agencyId : undefined,
    });
    onClose();
  });

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <div className="w-128 bg-white rounded-xl shadow  flex-col justify-start items-start inline-flex">
      <div className="self-stretch px-6 justify-start items-start gap-6 inline-flex">
        <div className="grow shrink basis-0 py-5 flex-col justify-start items-start gap-2 inline-flex">
          <h1 className="self-stretch text-base-md font-medium leading-snug text-secondary-black">
            Edit advertiser
          </h1>
        </div>
      </div>
      <div className="px-6 pt-3 pb-6 gap-6 stretch-to-form">
        <div className="self-stretch flex-col justify-start items-start gap-1.5 flex">
          <form
            onSubmit={handleFormSubmit}
            className="w-full gap-4 flex flex-col justify-start items-start"
          >
            <Field
              label={'Advertiser name'}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  invalid={invalid}
                  placeholder="Enter advertiser name..."
                  maxLength={MAX_LENGTH}
                />
              )}
              control={control}
              name={'name'}
              error={errors.name?.message}
              secondaryLabel={
                <div className="text-base-xs leading-tight font-medium flex items-center text-primary-gray-500">
                  {`${watch('name').length}/${MAX_LENGTH}`}
                </div>
              }
            />

            {canMove && (
              <Field
                label={'Agency'}
                render={({ field }) => (
                  <Select
                    {...field}
                    className={'w-full'}
                    options={agencies.map((a) => ({ value: a.id, label: a.name }))}
                  />
                )}
                control={control}
                name={'agencyId'}
                error={errors.agencyId?.message}
              />
            )}

            {isMoving && (
              <Indicator variant="warning" size="lg">
                {`${watch('name') || 'This advertiser'} will move from ${agencyName(
                  currentAgencyId,
                )} to ${agencyName(targetAgencyId)} (${DSP_LABELS[dsp || ''] || dsp} agencies). ` +
                  `It leaves ${agencyName(currentAgencyId)}'s advertiser list, and users who only ` +
                  `have access through ${agencyName(currentAgencyId)} will no longer see it.`}
              </Indicator>
            )}

            {dsp === DSP_ADFORM && (
              <Field
                label={'Linked Xandr advertiser (optional)'}
                render={({ field }) => (
                  <Select
                    {...field}
                    className={'w-full'}
                    value={field.value || undefined}
                    onChange={(value) => field.onChange(value)}
                    options={xandrAdvertisers}
                    loading={isLoadingXandr}
                    disabled={isLoadingXandr}
                    allowClear
                  />
                )}
                control={control}
                name={'linkedAdvertiserId'}
                error={errors.linkedAdvertiserId?.message}
              />
            )}

            <Field
              label={'Currency'}
              render={({ field }) => (
                <Select
                  {...field}
                  className={'w-full'}
                  placeholder="Select currency..."
                  options={CURRENCY_OPTIONS}
                />
              )}
              control={control}
              name={'currency'}
              error={errors.currency?.message}
            />
          </form>

          <div className="stretch-to-form gap-3">
            {errorText && (
              <Indicator variant="danger" size="lg">
                {errorText}
              </Indicator>
            )}
          </div>
        </div>
      </div>
      <div className="self-stretch h-16 px-6 border-t border-primary-gray-100 justify-end items-center gap-2 inline-flex">
        <div className="justify-end items-center gap-2 flex">
          <Button size="md" variant="secondary" onClick={handleCancel} className="w-20">
            Cancel
          </Button>
          <Button
            size="md"
            variant="primary"
            disabled={!isValid || !isDirty}
            onClick={handleFormSubmit}
          >
            {isMoving ? 'Move advertiser' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};
